import { useEffect, useState, useRef } from "react";
import { CustomDropdown } from "../ui/CustomDropdown";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RootState } from "../../store/indexRedux";
import { useDispatch, useSelector } from "react-redux";
import { NavigationButtons } from "../ui/NavigationButtons";
import { SdpPoint } from "../../types/sdpTypes";
import * as Sentry from "@sentry/react";
import { getSessionFromCookie } from "../utils/cognito";
import {
  setInjections,
  setOfftakes,
  setLoadingInjections,
  setLoadingOfftakes,
  setErrorInjections,
  setErrorOfftakes,
  setInjectionSelection,
  setOfftakeSelection,
  setSiteName as setSiteNameGlobal,
  setSiteNameManuallyEdited,
  setFlexConfirmed,
  clearDownstreamSteps,
  prefillFromContractData,
} from "../../store/pvCurtailment";

type StepNavProps = {
  onNext?: () => void;
  onPrevious?: () => void;
  onSubmit?: () => void;
  onResetProgress?: () => void;
  onHardStop?: (active: boolean) => void;
};

interface ActiveContracts {
  bess?: {
    site_name?: string | null;
    installation_id?: string | null;
  };
  eboiler?: boolean;
  pvCurtailment?: boolean;
  pvCurtailmentHardStop?: boolean; // Add this for hard stop detection
}

function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.replace(/-(\w)/g, (_, c) => `-${c.toUpperCase()}`).replace(/^\w/, (c) => c.toUpperCase()))
    .join(" ");
}

function GeneralInfo({ onNext, onPrevious, onResetProgress, onHardStop }: StepNavProps) {
  // validation / touched
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [touched, setTouched] = useState({ injection: false, siteName: false });
  const prevInjectionAddressRef = useRef<string>("");
  const prevDetectedContractsRef = useRef<ActiveContracts | null>(null);
  const prevInjectionEANRef = useRef<string>("");
  const prefetchedInstallationIdRef = useRef<string | null>(null);

  // Contract detection & confirmation
  const [detectedContracts, setDetectedContracts] = useState<ActiveContracts | null>(null);
  const [pendingOfftakeInstallationId, setPendingOfftakeInstallationId] = useState<string | null>(null);

  const dispatch = useDispatch();
  const {
    injectionEAN,
    offtakeEAN,
    injections,
    offtakes,
    loadingInjections,
    loadingOfftakes,
    selectedInjection,
    siteName,
    siteNameManuallyEdited,
    flexConfirmed,
  } = useSelector((state: RootState) => state.pvCurtailment);
  const selectedCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);

  const handleNext = onNext ?? (() => {});
  const handlePrevious = onPrevious ?? (() => {});
  const navigate = useNavigate();

  const companyId =
    selectedCompany?.company_id ||
    JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id ||
    "0405005979";

  const injectionEANRef = useRef(injectionEAN);
  injectionEANRef.current = injectionEAN;

  const injectionsRef = useRef(injections);
  injectionsRef.current = injections;

  // Fetch injections once on mount / company change
  useEffect(() => {
    const token = getSessionFromCookie()?.getAccessToken().getJwtToken();
    dispatch(setLoadingInjections(true));
    axios
      .get(
        `${import.meta.env.VITE_API_SERVICES || "http://localhost:8001"}/api/v1/sdp/companies/${companyId}/injections?includeUnsigned=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        const normalized = (res.data as SdpPoint[]).map((p) => ({
          ...p,
          addressZip: String(p.addressZip),
        }));
        dispatch(setInjections(normalized));
        dispatch(setErrorInjections(null));

        const currentEAN = injectionEANRef.current;
        if (currentEAN) {
          const updatedInjection = normalized.find((inj) => inj.ean === currentEAN);
          if (updatedInjection) {
            dispatch(setInjectionSelection({ ean: currentEAN, injection: updatedInjection }));
          }
        }
      })
      .catch((error) => {
        dispatch(setInjections([]));
        dispatch(setErrorInjections(error?.message || "Failed to load injections"));
        Sentry.captureException(error, {
          tags: { feature: "pvcurtailment", component: "GeneralInfo", type: "api" },
          extra: {
            endpoint: `/api/v1/sdp/companies/${companyId}/injections`,
            companyId,
            status: error?.response?.status,
            data: error?.response?.data,
          },
        });
      })
      .finally(() => dispatch(setLoadingInjections(false)));
  }, [companyId, dispatch]);

  // Fetch offtakes when injection changes
  useEffect(() => {
    if (injectionEAN) {
      const token = getSessionFromCookie()?.getAccessToken().getJwtToken();
      dispatch(setOfftakeSelection({ ean: "", offtake: null }));
      dispatch(setLoadingOfftakes(true));
      axios
        .get(
          `${
            import.meta.env.VITE_API_SERVICES || "http://localhost:8001"
          }/api/v1/sdp/companies/${companyId}/injections/${injectionEAN}/offtakes?includeUnsigned=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) => {
          const normalized = (res.data as SdpPoint[]).map((p) => ({
            ...p,
            addressZip: String(p.addressZip),
          }));
          dispatch(setOfftakes(normalized));
          dispatch(setErrorOfftakes(null));

          const currentInjection = injectionsRef.current.find((i) => i.ean === injectionEAN);
          if (
            ((currentInjection?.hasBess === 1 && currentInjection?.bess?.installation_id) ||
              (currentInjection?.hasEboiler === 1 && currentInjection?.eboiler?.has_active_contract === 1)) &&
            normalized.length === 1
          ) {
            dispatch(setOfftakeSelection({ ean: normalized[0].ean, offtake: normalized[0] }));
          }
        })
        .catch((error) => {
          dispatch(setOfftakes([]));
          dispatch(setErrorOfftakes(error?.message || "Failed to load offtakes"));
          Sentry.captureException(error, {
            tags: { feature: "pvcurtailment", component: "GeneralInfo", type: "api" },
            extra: {
              endpoint: `/api/v1/sdp/companies/${companyId}/injections/${injectionEAN}/offtakes`,
              companyId,
              injectionEAN,
              status: error?.response?.status,
              data: error?.response?.data,
            },
          });
        })
        .finally(() => dispatch(setLoadingOfftakes(false)));
    } else {
      dispatch(setOfftakes([]));
      dispatch(setOfftakeSelection({ ean: "", offtake: null }));
    }
  }, [injectionEAN, companyId, dispatch]);

  // Detect active contracts when injection changes
  useEffect(() => {
    if (selectedInjection) {
      const contracts: ActiveContracts = {};

      // Check for BESS - must have hasBess=1 AND bess object with installation_id AND has_active_contract=1
      if (
        selectedInjection.hasBess === 1 &&
        selectedInjection.bess?.installation_id &&
        selectedInjection.bess?.has_active_contract === 1
      ) {
        contracts.bess = {
          site_name: selectedInjection.bess.site_name ?? undefined,
          installation_id: selectedInjection.bess.installation_id ?? undefined,
        };
      }

      // Check for eboiler - must have hasEboiler=1 AND eboiler object with has_active_contract=1
      if (selectedInjection.hasEboiler === 1 && selectedInjection.eboiler?.has_active_contract === 1) {
        contracts.eboiler = true;
      }

      // Check for PV curtailment - must have hasPvCurtailment=1 AND pvCurtailment object with has_active_contract=1
      if (selectedInjection.hasPvCurtailment === 1 && selectedInjection.pvCurtailment?.has_active_contract === 1) {
        contracts.pvCurtailment = true;
        contracts.pvCurtailmentHardStop = true; // Mark as hard stop - cannot proceed
      }

      // Check if injection EAN has changed (comparing with ref, not Redux state)
      // On initial mount, prevInjectionEANRef will be empty, so we check against Redux state
      const isInitialMount = prevInjectionEANRef.current === "";
      const injectionEANChanged = !isInitialMount && prevInjectionEANRef.current !== selectedInjection.ean;

      // Only show detection if there are active contracts
      if (Object.keys(contracts).length > 0) {
        // Compare with previous contracts to see if they've changed
        const isFirstDetection = prevDetectedContractsRef.current === null;
        const contractsChanged =
          !isFirstDetection && JSON.stringify(prevDetectedContractsRef.current) !== JSON.stringify(contracts);

        setDetectedContracts(contracts);
        prevDetectedContractsRef.current = contracts;
        prevInjectionEANRef.current = selectedInjection.ean;

        // Only reset confirmation if:
        // 1. Injection EAN changed (and it's not initial mount), OR
        // 2. Contracts changed (and it's not the first detection)
        // This preserves the confirmation when:
        // - Navigating back to the same injection (same EAN, same contracts)
        // - Refreshing the page (initial mount with same injection)
        if (injectionEANChanged || contractsChanged) {
          dispatch(setFlexConfirmed(false));
        }
        // On initial mount with same injection, preserve the existing confirmation state from Redux/localStorage
      } else {
        // Only reset if we previously had contracts or injection changed (not on initial mount)
        if ((prevDetectedContractsRef.current !== null || injectionEANChanged) && !isInitialMount) {
          dispatch(setFlexConfirmed(false));
        }
        setDetectedContracts(null);
        prevDetectedContractsRef.current = null;
        prevInjectionEANRef.current = selectedInjection.ean;
      }
    } else {
      // Only reset if we previously had contracts (not on initial mount with no injection)
      if (prevDetectedContractsRef.current !== null && prevInjectionEANRef.current !== "") {
        dispatch(setFlexConfirmed(false));
      }
      setDetectedContracts(null);
      prevDetectedContractsRef.current = null;
      prevInjectionEANRef.current = "";
    }
  }, [selectedInjection, dispatch]);

  // Reset prefetch tracker when injection changes so the new injection gets prefilled
  useEffect(() => {
    prefetchedInstallationIdRef.current = null;
  }, [injectionEAN]);

  // Prefill steps 2 & 3 from an existing unsigned PV curtailment contract
  useEffect(() => {
    const pvc = selectedInjection?.pvCurtailment;
    const installationId = pvc?.installation_id;

    if (pvc?.contract_status !== "unsigned" || !installationId) return;
    if (prefetchedInstallationIdRef.current === installationId) return; // already fetched this session

    prefetchedInstallationIdRef.current = installationId;

    const token = getSessionFromCookie()?.getAccessToken().getJwtToken();
    axios
      .get(
        `${import.meta.env.VITE_API_SERVICES || "http://localhost:8001"}/api/v1/sdp/site-steering/${installationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        dispatch(prefillFromContractData(res.data));
        // Use "none" as sentinel when contract had no offtake
        setPendingOfftakeInstallationId(res.data.offtake_installation_id || "none");
      })
      .catch(() => {
        // silent — user fills manually if fetch fails
      });
  }, [selectedInjection, dispatch]);

  // Deferred offtake selection after contract prefill — fires once offtakes finish loading
  useEffect(() => {
    if (!pendingOfftakeInstallationId || loadingOfftakes) return;
    if (pendingOfftakeInstallationId === "none") {
      dispatch(setOfftakeSelection({ ean: "none", offtake: null }));
    } else {
      const matched = offtakes.find((o) => o.installation_id === pendingOfftakeInstallationId);
      if (matched) {
        dispatch(setOfftakeSelection({ ean: matched.ean, offtake: matched }));
      }
    }
    setPendingOfftakeInstallationId(null);
  }, [pendingOfftakeInstallationId, offtakes, loadingOfftakes, dispatch]);

  // Auto-suggest offtake if BESS or E-Boiler is detected
  // Check selectedInjection directly to avoid timing issues with detectedContracts state
  useEffect(() => {
    const hasBess = selectedInjection?.hasBess === 1 && selectedInjection?.bess?.installation_id;
    const hasEboiler = selectedInjection?.hasEboiler === 1 && selectedInjection?.eboiler?.has_active_contract === 1;
    if ((hasBess || hasEboiler) && !loadingOfftakes && offtakes.length > 0 && !offtakeEAN) {
      // If only one offtake exists from the API, auto-select it
      if (offtakes.length === 1) {
        console.log("Auto-selecting offtake:", offtakes[0].ean);
        dispatch(setOfftakeSelection({ ean: offtakes[0].ean, offtake: offtakes[0] }));
      }
    }
  }, [selectedInjection, offtakes, offtakeEAN, dispatch, loadingOfftakes]);
  const injectionOptions = injections.map((inj) => ({
    value: inj.ean,
    label: inj.ean,
    address: `${inj.addressStreet} ${inj.addressNumber}, ${inj.addressZip} ${inj.addressCity}`,
  }));

  const selectedInjectionAddress = selectedInjection
    ? `${selectedInjection.addressStreet} ${selectedInjection.addressNumber}, ${selectedInjection.addressZip} ${selectedInjection.addressCity}`
    : "";

  const offtakeOptions = [
    ...offtakes.map((off) => ({
      value: off.ean,
      label: off.ean,
      address: `${off.addressStreet} ${off.addressNumber}, ${off.addressZip} ${off.addressCity}`,
    })),
    // Only show "None" option if there's no active BESS or E-Boiler
    ...(!detectedContracts?.bess && !detectedContracts?.eboiler
      ? [
          {
            value: "none",
            label: "None - Continue without offtake",
            address: "",
          },
        ]
      : []),
  ];
  const isOfftakeMissing =
    flexConfirmed &&
    (detectedContracts?.bess || detectedContracts?.eboiler) &&
    (!offtakeEAN || offtakeEAN === "none");
  const suggestedSiteName = selectedInjection
    ? (selectedInjection.site_name?.trim() || toTitleCase(`${selectedInjection.addressStreet} ${selectedInjection.addressCity}`))
    : "";

  // Auto-suggest site name from injection's site_name (or fall back to address).
  // Never overwrite if the user has manually edited the site name.
  useEffect(() => {
    if (siteNameManuallyEdited) return;

    if (suggestedSiteName && suggestedSiteName !== prevInjectionAddressRef.current) {
      const isSiteNameEmpty = !siteName || siteName.trim() === "";
      if (isSiteNameEmpty || siteName !== suggestedSiteName) {
        dispatch(setSiteNameGlobal(suggestedSiteName));
      }
      prevInjectionAddressRef.current = suggestedSiteName;
    } else if (!suggestedSiteName) {
      prevInjectionAddressRef.current = "";
    }
  }, [suggestedSiteName, siteName, siteNameManuallyEdited, dispatch]);

  // validation flags
  const isInjectionMissing = !injectionEAN || injectionEAN === "";
  const isSiteNameMissing = !siteName || siteName.trim() === "";
  // Block if there are detected contracts and user hasn't confirmed
  const hasUnconfirmedFlex = detectedContracts && !flexConfirmed && !detectedContracts.pvCurtailmentHardStop;
  // Block if PV Curtailment is active (hard stop)
  const hasActivePvCurtailment = detectedContracts?.pvCurtailmentHardStop === true;

  useEffect(() => {
    onHardStop?.(hasActivePvCurtailment);
  }, [hasActivePvCurtailment, onHardStop]);

  // show errors only after touch or attempt to go next
  const showInjectionError = (attemptedNext || touched.injection) && isInjectionMissing;
  const showSiteNameError = (attemptedNext || touched.siteName) && isSiteNameMissing;

  // programmatic block evaluation
  const blockNext = isInjectionMissing || isSiteNameMissing || hasUnconfirmedFlex || hasActivePvCurtailment;

  function handleInjectionChange(val: string) {
    const previousEAN = injectionEAN;
    const inj = injections.find((i) => i.ean === val) || null;
    dispatch(setInjectionSelection({ ean: val, injection: inj }));
    setTouched((s) => ({ ...s, injection: true }));

    if (previousEAN && previousEAN !== val) {
      dispatch(clearDownstreamSteps());
      onResetProgress?.();
    }
  }

  function handleOfftakeChange(val: string) {
    const off = offtakes.find((o) => o.ean === val) || null;
    dispatch(setOfftakeSelection({ ean: val, offtake: off }));
  }

  function handleSiteNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    dispatch(setSiteNameGlobal(v));
    dispatch(setSiteNameManuallyEdited(true));
    setTouched((s) => ({ ...s, siteName: true }));
  }

  // tryNext: mark attempted and touched, and proceed only if valid
  function tryNext() {
    setAttemptedNext(true);
    setTouched({ injection: true, siteName: true });
    if (!blockNext) {
      handleNext();
    }
  }

  return (
    <div>
      <div className="mb-12">
        <h1 className="text-primary-600 dark:text-white text-3xl font-semibold mb-6">General Information</h1>
        <p className="text-primary-500 dark:text-gray-300 font-normal text-base text-md">
          Please provide the general information for this site.
        </p>
      </div>

      <div className="bg-white dark:bg-primary-900 p-12 rounded-lg w-full border-[2px] border-gray-100 dark:border-primary-800">
        <div className="text-2xl text-primary-600 dark:text-white font-semibold mb-8">
          <h2>General information</h2>
        </div>

        <form className="flex flex-col gap-6 text-sm">
          <div className="flex gap-10">
            {/* Injection EAN */}
            <div className="flex flex-1 flex-col gap-3">
              <label className="font-semibold text-primary-600 dark:text-white" htmlFor="injection-ean">
                Injection EAN <span className="text-red-600">*</span>
              </label>
              <div className="flex">
                <CustomDropdown
                  options={injectionOptions}
                  value={injectionEAN}
                  onChange={(val) => handleInjectionChange(val)}
                  placeholder={loadingInjections ? "Loading..." : "Select Injection EAN"}
                  className="flex-1"
                  disabled={!loadingInjections && injections.length === 0}
                />
                <button
                  type="button"
                  className="bg-primary-600 text-white px-4 text-3xl hover:bg-primary-700 rounded-r"
                  onClick={() => navigate("/addservice?intent=inj")}
                >
                  +
                </button>
              </div>

              {showInjectionError && (
                <p className="text-xs text-red-600 mt-2">Please select an Injection EAN to continue.</p>
              )}

              {!loadingInjections && injections.length === 0 && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-xs text-amber-700 mb-2">No injection EAN registered for your company yet.</p>
                  <a
                    href="/addservice?intent=inj"
                    className="text-xs font-medium text-amber-600 hover:text-amber-700 underline flex items-center gap-1"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/addservice?intent=inj");
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Register your injection with Yuso
                  </a>
                </div>
              )}

              {injectionEAN && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{selectedInjectionAddress}</div>}
            </div>

            {/* Offtake EAN */}
            <div className="flex flex-1 flex-col gap-3">
              {isOfftakeMissing && (
                <p className="text-xs text-red-600 mt-2">
                  Offtake EAN is required when an active flex service is confirmed. Please select an offtake.
                </p>
              )}
              <label className="font-semibold text-primary-600 dark:text-white flex items-center gap-1.5" htmlFor="offtake-ean">
                Offtake EAN
                <div className="tooltip tooltip-right" data-tip="Maximize the flexibility potential of your solar PV installation with a Yuso offtake contract. This is optional and can be added later.">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-primary-400 cursor-help">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                  </svg>
                </div>
              </label>
              <div className="flex">
                <CustomDropdown
                  options={offtakeOptions}
                  value={offtakeEAN}
                  onChange={(val) => handleOfftakeChange(val)}
                  placeholder={
                    injectionEAN
                      ? loadingOfftakes
                        ? "Loading..."
                        : offtakeOptions.length > 0
                        ? "Select Offtake EAN"
                        : "No matching Offtake EAN"
                      : "Select Injection EAN first"
                  }
                  className="flex-1"
                  disabled={!injectionEAN || injections.length === 0}
                />
                <button
                  type="button"
                  className="bg-primary-600 text-white px-4 text-3xl hover:bg-primary-700 rounded-r"
                  onClick={() => navigate("/addservice?intent=off")}
                >
                  +
                </button>
              </div>
              {offtakeEAN && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {offtakeOptions.find((opt) => opt.value === offtakeEAN)?.address}
                </div>
              )}
              {injectionEAN && !loadingOfftakes && offtakes.length === 0 && (
                <div className="mt-2 p-3 bg-primary-50 dark:bg-primary-800 border border-primary-200 dark:border-primary-700 rounded">
                  <p className="text-xs text-primary-700 dark:text-gray-300 mb-2">No offtake SDP registered for this address yet.</p>
                  <a
                    href="/addservice?intent=off"
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 underline flex items-center gap-1"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/addservice?intent=off");
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Register your offtake with Yuso
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Site name */}
          <div className="flex flex-col gap-3">
            <label className="font-semibold text-primary-600 dark:text-white" htmlFor="site-name">
              Site name <span className="text-red-600">*</span>
            </label>
            <input
              id="site-name"
              className="border p-3 text-primary-500 dark:text-white dark:bg-primary-800 dark:border-primary-700"
              type="text"
              value={siteName ?? ""}
              onChange={handleSiteNameChange}
              onBlur={() => setTouched((s) => ({ ...s, siteName: true }))}
            />
            {(attemptedNext || touched.siteName) && showSiteNameError && (
              <p className="text-xs text-red-600 mt-2">Please enter a site name.</p>
            )}
            {suggestedSiteName && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {selectedInjection?.site_name?.trim()
                  ? "Using existing site name. You can change this."
                  : `Suggested: ${suggestedSiteName}. You can change this.`}
              </span>
            )}
          </div>

          {/* Active Contracts Detection Box */}
          {detectedContracts && (
            <div
              className={`mt-8 p-4 border-l-4 rounded-lg ${
                detectedContracts.pvCurtailmentHardStop ? "bg-red-50 border-red-500" : "bg-amber-50 border-amber-500"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 pt-0.5">
                  {detectedContracts.pvCurtailmentHardStop ? (
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  {detectedContracts.pvCurtailmentHardStop ? (
                    <>
                      <h3 className="text-sm font-semibold text-red-900 mb-2">
                        Active PV Curtailment Contract Detected
                      </h3>
                      <p className="text-xs text-red-800 mb-3">
                        This location already has an active PV Curtailment contract. You cannot proceed with creating a
                        new PV Curtailment contract.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-sm font-semibold text-amber-900 mb-2">Active Contracts Detected</h3>
                      <p className="text-xs text-amber-800 mb-3">
                        This location already has active flex services. To add PV Curtailment, we'll combine them into a
                        single contract.
                      </p>
                    </>
                  )}

                  {/* List detected services */}
                  <div
                    className={`space-y-2 mb-4 p-3 rounded border ${
                      detectedContracts.pvCurtailmentHardStop ? "bg-white border-red-200" : "bg-white border-amber-200"
                    }`}
                  >
                    {detectedContracts.bess && (
                      <div
                        className={`flex items-center text-xs ${
                          detectedContracts.pvCurtailmentHardStop ? "text-red-900" : "text-amber-900"
                        }`}
                      >
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            detectedContracts.pvCurtailmentHardStop ? "bg-red-500" : "bg-amber-500"
                          }`}
                        ></span>
                        <strong>BESS:</strong> {detectedContracts.bess.site_name} (
                        {detectedContracts.bess.installation_id})
                      </div>
                    )}
                    {detectedContracts.eboiler && (
                      <div
                        className={`flex items-center text-xs ${
                          detectedContracts.pvCurtailmentHardStop ? "text-red-900" : "text-amber-900"
                        }`}
                      >
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            detectedContracts.pvCurtailmentHardStop ? "bg-red-500" : "bg-amber-500"
                          }`}
                        ></span>
                        <strong>E-Boiler</strong> active
                      </div>
                    )}
                    {detectedContracts.pvCurtailment && (
                      <div
                        className={`flex items-center text-xs ${
                          detectedContracts.pvCurtailmentHardStop ? "text-red-900" : "text-amber-900"
                        }`}
                      >
                        <span
                          className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            detectedContracts.pvCurtailmentHardStop ? "bg-red-500" : "bg-amber-500"
                          }`}
                        ></span>
                        <strong>PV Curtailment</strong> active
                      </div>
                    )}
                  </div>

                  {/* Only show confirmation checkbox if NOT a hard stop */}
                  {!detectedContracts.pvCurtailmentHardStop && (
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={flexConfirmed}
                        onChange={(e) => dispatch(setFlexConfirmed(e.target.checked))}
                        className="w-4 h-4 mt-0.5 accent-amber-600"
                      />
                      <span className="text-xs text-amber-900">
                        I confirm that I want to add PV Curtailment to the existing flex services. The current contract
                        will be updated with the new terms.
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Show error message when trying to proceed with active PV Curtailment */}
        {attemptedNext && hasActivePvCurtailment && (
          <p className="text-sm text-red-600 mt-4 mb-2 font-semibold">
            Cannot proceed: This location has an active PV Curtailment contract. Please contact support if you need to
            modify your existing contract.
          </p>
        )}

        {attemptedNext && hasUnconfirmedFlex && !hasActivePvCurtailment && (
          <p className="text-sm text-red-600 mt-4 mb-2">
            Please confirm that you want to add PV Curtailment to the existing flex services before continuing.
          </p>
        )}

        <NavigationButtons
          currentStep={1}
          totalSteps={4}
          onPrevious={handlePrevious}
          onNext={tryNext}
          nextDisabled={blockNext}
        />
      </div>
    </div>
  );
}

export default GeneralInfo;
